<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { NCard, NDataTable } from 'naive-ui'
import type { DataTableBaseColumn, DataTableColumn, PaginationProps } from 'naive-ui'
import { type Ref, computed, ref } from 'vue'
import { debounce } from '@/utils/functions/debounce'
import { SvgIcon } from '@/components/common'
import EmptyDisplay from '@/components/common/EmptyDisplay.vue'
import { PAGINATION } from '@/constants'

const props = withDefaults(defineProps<{
  columns: DataTableColumn<any>[]
  dataSource: any[]
  title?: string
  pagination?: any
  loading?: boolean
  showQuickSearch?: boolean
  showHeader?: boolean
  showHeaderTop?: boolean
  showHeaderContent?: boolean
  showHeaderFooter?: boolean
  searchPlaceholder?: string
  minWidthQuickSearch?: string
  rowKey?: (row: object) => number | string
  renderCell?: RenderCellFunction
  bordered?: boolean
  striped?: boolean
  singleLine?: boolean
  size?: 'small' | 'medium' | 'large'
  maxHeight?: number | string
  scrollX?: number | string
  scrollY?: number | string
  tableLayout?: 'auto' | 'fixed'
  virtualScroll?: boolean
  rowClassName?: string | ((row: any, index: number) => string)
  remote?: boolean
  rowProps?: RowPropsFunction
  showCard?: boolean
  classTable?: string
  checkedRowKeys?: any[]
}>(), {
  loading: false,
  rowKey: undefined,
  bordered: true,
  striped: true,
  singleLine: true,
  size: 'medium',
  virtualScroll: false,
  remote: false,
  showCard: true,
  classTable: '',
  checkedRowKeys: () => [],
  pagination: undefined,
})

const emit = defineEmits<{
  (e: 'getQuickSearch', value: string): void
  (e: 'rowClick', row: any): void
  (e: 'sortChange', sorter: any): void
  (e: 'update:checked-row-keys', keys: (string | number)[]): void
  (e: 'update:page', page: number): void
  (e: 'update:pageSize', pageSize: number): void
  (e: 'update:sorter', sorter: any): void
  (e: 'update:filters', filters: any): void
}>()

// Disable automatic attribute inheritance to manually control where attrs are applied
defineOptions({
  inheritAttrs: false,
})

type RenderCellFunction = (value: any, rowData: object, column: DataTableBaseColumn) => any
type RowPropsFunction = (rowData: object, rowIndex: number) => any

const quickSearch = defineModel<string>('quickSearchValue', {
  default: '',
}) as Ref<string>

const paginationDataTable = ref<PaginationProps>({
  ...PAGINATION,
  pageSize: 10,
})

// Computed pagination: handle object, boolean, or default
const computedPagination = computed(() => {
  // If pagination is explicitly false, hide it
  if (props.pagination === false)
    return false

  // If pagination is an object, use it
  if (props.pagination && typeof props.pagination === 'object')
    return props.pagination

  // If pagination is true or undefined, use default
  return paginationDataTable.value
})

const { t } = useI18n()

const onQuickSearch = debounce(() => {
  emit('getQuickSearch', quickSearch.value.trim())
}, 1500)

const reload = () => {
  emit('getQuickSearch', quickSearch.value.trim())
}

const resetSearch = () => {
  quickSearch.value = ''
  emit('getQuickSearch', '')
}

// Handle pagination updates for default pagination
const handleUpdatePage = (page: number) => {
  // If using default pagination, update it
  if (!props.pagination || props.pagination === true)
    paginationDataTable.value.page = page

  emit('update:page', page)
}

const handleUpdatePageSize = (pageSize: number) => {
  // If using default pagination, update it
  if (!props.pagination || props.pagination === true) {
    paginationDataTable.value.page = 1
    paginationDataTable.value.pageSize = pageSize
  }
  emit('update:pageSize', pageSize)
}

defineExpose({
  reload,
  resetSearch,
})
</script>

<template>
  <component :is="showCard ? NCard : 'div'" :class="showCard ? '!rounded-lg shadow' : ''" :size="showCard ? 'small' : undefined">
    <div class="flex flex-col gap-4 w-full">
      <div v-if="showHeader" class="flex flex-col gap-4">
        <div v-if="title" class="font-normal text-[24px] leading-[32px]">
          <span>{{ title }}</span>
        </div>
        <div v-else-if="showHeaderTop">
          <slot name="headerTop" />
        </div>

        <div v-if="showHeaderContent">
          <slot name="headerContent" />
        </div>

        <div v-if="showHeaderFooter" class="flex justify-between items-center flex-wrap gap-2">
          <div class="flex items-center gap-2 flex-wrap">
            <div v-if="showQuickSearch">
              <AppInput
                v-model:value="quickSearch"
                :placeholder="searchPlaceholder || t('table.quickSearchPlaceholder')"
                :style="{ minWidth: minWidthQuickSearch }"
                :maxlength="255"
                clearable
                class=" shadow-[0_2px_6px_0_rgba(0,0,0,0.08)]"
                size="medium"
                @input="onQuickSearch"
              >
                <template #prefix>
                  <SvgIcon icon="ion:search-outline" class="text-lg" />
                </template>
              </AppInput>
            </div>
            <div class="flex items-center gap-2">
              <slot name="headerAfterQuickSearch" />
            </div>
          </div>
          <div class="flex items-center gap-2">
            <slot name="headerExtra" />
          </div>
        </div>
      </div>

      <!--    <div class="flex justify-between items-center"> -->
      <!--      <slot name="leftActions" /> -->
      <!--      <slot name="rightActions" /> -->
      <!--    </div> -->
      <NDataTable
        :remote="remote"
        :columns="columns"
        :data="dataSource"
        :pagination="computedPagination"
        :loading="loading"
        :row-key="rowKey"
        :render-cell="renderCell"
        :bordered="bordered"
        :striped="striped"
        :single-line="singleLine"
        :size="size"
        :max-height="maxHeight"
        :scroll-x="scrollX"
        :scroll-y="scrollY"
        :table-layout="tableLayout"
        :virtual-scroll="virtualScroll"
        :row-class-name="rowClassName"
        :row-props="rowProps"
        :show-item-count="true"
        :class="classTable"
        :checked-row-keys="checkedRowKeys"
        v-bind="$attrs"
        @row-click="(row: any) => emit('rowClick', row)"
        @update:sorter="sorter => emit('update:sorter', sorter)"
        @update:checked-row-keys="keys => emit('update:checked-row-keys', keys)"
        @update:page="handleUpdatePage"
        @update:pageSize="handleUpdatePageSize"
        @update:filters="filters => emit('update:filters', filters)"
      >
        <template v-if="$slots.header">
          <slot name="header" />
        </template>
        <template v-if="$slots.footer" #footer>
          <slot name="footer" />
        </template>
        <template v-if="$slots.loading" #loading>
          <slot name="loading" />
        </template>
        <template #empty>
          <div class="w-full h-full">
            <slot name="empty">
              <EmptyDisplay class="dark:!bg-transparent w-full h-full " />
            </slot>
          </div>
        </template>
      </NDataTable>
    </div>
  </component>
</template>

<style scoped>
/* Remove default empty padding from Naive UI data table */
:deep(.n-data-table-empty) {
  padding: 0 !important;
}
</style>
